/**
 * Router guard factory for TanStack Router `beforeLoad` (03-07 §3).
 *
 * The package stays framework-free: it does not import `@tanstack/react-router`
 * and knows nothing about the auth session's shape in the router context. The app
 * wires it once — supplying how to read a `Subject` from its `beforeLoad` context
 * and the TSR `redirect`/`notFound` helpers to throw — and gets a
 * `requirePermission(key)` guard back.
 *
 * Client checks are UI show/hide only; the server remains authoritative (03-07 §1).
 */

import { hasPermission, type Subject } from './checks';
import type { PermissionKey } from './matrix.config';
import { canAccessRoute, type RouteId } from './routes';

export interface RequirePermissionOptions {
  /** Redirect target passed to the configured `redirect` when the check fails. */
  redirectTo?: string;
  /** Throw the configured `notFound()` instead of a redirect. */
  notFound?: boolean;
}

export interface PermissionGuardConfig<Ctx> {
  /** Extract the current subject from the router `beforeLoad` context. */
  getSubject: (ctx: Ctx) => Subject;
  /**
   * Build the value to throw for a redirect (e.g. TSR `redirect({ to })`).
   *
   * `ctx` is the same `beforeLoad` context the guard was given, so the target can carry where the
   * subject was heading — an access-denied screen that names the destination is far more useful
   * than a bare one, and only the caller knows how to read a location out of its own context.
   */
  redirect: (redirectTo: string | undefined, ctx: Ctx) => unknown;
  /** Build the value to throw for a not-found (e.g. TSR `notFound()`). Required to use `{ notFound: true }`. */
  notFound?: () => unknown;
}

/**
 * Build a `requirePermission` guard bound to `config`. The returned guard is a
 * `beforeLoad` handler: it resolves the subject, and throws the configured
 * redirect (or not-found) when the subject lacks `key`.
 */
export function createPermissionGuard<Ctx>(config: PermissionGuardConfig<Ctx>) {
  return function requirePermission(key: PermissionKey, options?: RequirePermissionOptions) {
    return (ctx: Ctx): void => {
      if (hasPermission(config.getSubject(ctx), key)) return;
      throw refuse(config, ctx, options);
    };
  };
}

/**
 * Build a `requireRoute` guard bound to `config` — the same shape as `requirePermission`, but
 * keyed on a `RouteId` so the decision comes from `ROUTE_PERMISSIONS` rather than from a key the
 * caller names.
 *
 * This is the form a router actually needs. Most routes grant on *any one* of several keys
 * (`/devices/$id` on `Device.View` **or** `Device.ViewAll`), which `requirePermission` cannot
 * express: the route file would have to re-list the keys, becoming a second copy of the map that
 * drifts from the one the navigation menu reads. Keyed on the route id, the guard and the menu
 * cannot disagree.
 *
 * It belongs in `beforeLoad` rather than in the component: refusing there also stops the route's
 * loader, so a screen the subject cannot open never fires the request that would 403 anyway.
 */
export function createRouteGuard<Ctx>(config: PermissionGuardConfig<Ctx>) {
  return function requireRoute(routeId: RouteId, options?: RequirePermissionOptions) {
    return (ctx: Ctx): void => {
      if (canAccessRoute(routeId, config.getSubject(ctx))) return;
      throw refuse(config, ctx, options);
    };
  };
}

/** The value a refused guard throws, shared by both factories. */
function refuse<Ctx>(
  config: PermissionGuardConfig<Ctx>,
  ctx: Ctx,
  options?: RequirePermissionOptions,
) {
  if (options?.notFound) {
    if (!config.notFound) {
      throw new Error(
        'requireRoute/requirePermission: { notFound: true } requires a notFound() in the guard config',
      );
    }
    return config.notFound();
  }
  return config.redirect(options?.redirectTo, ctx);
}
